import React, { useEffect, useState } from "react";
import { cashAPI } from "../services/api";
import { useI18n } from "../i18n";
import useShopCurrency from "../hooks/useShopCurrency";
import { formatMoney } from "../pos/utils/money";
import { formatDateTime } from "../utils/dates";
import EmptyState from "../components/EmptyState";

/**
 * Every day the till's drawer was open: what it was opened with, what it
 * should have held at closing, what was actually counted, what went home and
 * what was left for the next day.
 *
 * Both counts are blind at the till, so a difference here is a real one —
 * the first place to look when the money and the sales disagree.
 */
const CashDrawer = () => {
  const { t } = useI18n();
  const currency = useShopCurrency();
  const money = (value) => formatMoney(value, currency);

  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [open, setOpen] = useState({});

  useEffect(() => {
    cashAPI
      .sessions({ limit: 60 })
      .then(({ data }) => setSessions(data))
      .catch((err) => setError(err.response?.data?.message || t("cash.loadFailed")))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = async (session) => {
    if (open[session.id]) {
      setOpen((current) => ({ ...current, [session.id]: null }));
      return;
    }

    setOpen((current) => ({ ...current, [session.id]: { loading: true } }));
    try {
      const { data } = await cashAPI.session(session.id);
      setOpen((current) => ({ ...current, [session.id]: data }));
    } catch {
      setOpen((current) => ({ ...current, [session.id]: { failed: true } }));
    }
  };

  const differenceTone = (value) =>
    value === 0 ? "text-emerald-700" : value < 0 ? "text-red-700" : "text-amber-700";

  const signed = (value) => `${value > 0 ? "+" : ""}${money(value)}`;

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">{t("cash.title")}</h1>
        <p className="text-gray-600">{t("cash.subtitle")}</p>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
      )}

      <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6">
        {loading ? (
          <p className="text-gray-500 py-8 text-center">{t("common.loading")}</p>
        ) : sessions.length === 0 ? (
          <EmptyState screen="cash" message={t("cash.none")} />
        ) : (
          <ul className="divide-y divide-gray-100">
            {sessions.map((session) => {
              const detail = open[session.id];
              const closed = session.status === "closed";

              return (
                <li key={session.id} className="py-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900">
                        {formatDateTime(session.opened_at)}
                        {!closed && (
                          <span className="ms-2 text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
                            {t("cash.openNow")}
                          </span>
                        )}
                      </p>
                      <p className="text-sm text-gray-500">
                        {t("cash.openedBy", {
                          name: session.opened_by_name || "—",
                          amount: money(session.opening_count),
                        })}
                        {session.opening_difference !== 0 && (
                          <span className={`ms-1 font-semibold ${differenceTone(session.opening_difference)}`}>
                            ({t("cash.expectedWas", { amount: money(session.carried_over) })},{" "}
                            <span dir="ltr">{signed(session.opening_difference)}</span>)
                          </span>
                        )}
                      </p>
                      {closed && (
                        <p className="text-sm text-gray-500">
                          {t("cash.closedBy", {
                            name: session.closed_by_name || "—",
                            time: formatDateTime(session.closed_at),
                          })}
                        </p>
                      )}
                    </div>

                    <dl className="grid grid-cols-2 sm:grid-cols-[repeat(4,8.5rem)] gap-x-4 gap-y-1 text-sm">
                      <div>
                        <dt className="text-gray-500">{t("cash.expected")}</dt>
                        <dd className="font-semibold tabular-nums">{money(session.expected)}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">{t("cash.counted")}</dt>
                        <dd className="font-semibold tabular-nums">
                          {closed ? money(session.counted_cash) : "—"}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">{t("cash.difference")}</dt>
                        <dd
                          className={`font-semibold tabular-nums ${
                            closed ? differenceTone(session.difference) : ""
                          }`}
                          dir="ltr"
                        >
                          {closed ? signed(session.difference) : "—"}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">{t("cash.takenHome")}</dt>
                        <dd className="font-semibold tabular-nums">
                          {closed
                            ? t("cash.takenAndLeft", {
                                taken: money(session.taken_out),
                                left: money(session.left_in_drawer),
                              })
                            : "—"}
                        </dd>
                      </div>
                    </dl>
                  </div>

                  <button
                    type="button"
                    onClick={() => toggle(session)}
                    className="mt-2 text-sm font-semibold text-primary-700 hover:underline"
                  >
                    {detail ? t("cash.hideMovements") : t("cash.showMovements")}
                  </button>

                  {detail && (
                    <div className="mt-2 rounded-lg bg-gray-50 p-3 text-sm">
                      {detail.loading ? (
                        <p className="text-gray-500">{t("common.loading")}</p>
                      ) : detail.failed ? (
                        <p className="text-red-700">{t("cash.loadFailed")}</p>
                      ) : (
                        <ul className="space-y-1">
                          <li className="flex justify-between gap-3">
                            <span>{t("pos.drawer.lines.sales", { count: detail.sales })}</span>
                            <span className="tabular-nums">+{money(detail.cash_sales)}</span>
                          </li>
                          {detail.movements.map((movement) => (
                            <li key={movement.id} className="flex justify-between gap-3">
                              <span className="min-w-0">
                                {movement.reason
                                  ? t(`pos.drawer.reasons.${movement.reason}`)
                                  : t(`pos.drawer.movements.${movement.type}`)}
                                {movement.note && ` · ${movement.note}`}
                                <span className="text-gray-400">
                                  {" "}
                                  · {movement.created_by_name || "—"} · {formatDateTime(movement.created_at)}
                                </span>
                              </span>
                              <span className="tabular-nums shrink-0" dir="ltr">
                                {["cash_in", "tab_payment"].includes(movement.type) ? "+" : "−"}
                                {money(movement.amount)}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default CashDrawer;
